import React, {useState} from "react";
import {Button, Spin, Table, Tabs, Tooltip, Typography} from "antd";
import type {ColumnsType, TableProps} from "antd/es/table";
import {
    getArchiveReasonLabel,
    getSuspensionReasonLabel,
    SendStatusLabel,
    SendStatusType,
    SimulatedFileTS,
    StatusCodeE,
} from "../../model/models";
import {UtilsService} from "../../services/Utils.service";
import UserDetailsButton from "../userDetails/UserDetailsButton";
import "./Cruscotto.css";
import {TemplateUtils} from "../../services/utils/TemplateUtils";
import {EyeOutlined, MinusOutlined} from "@ant-design/icons";
import {SendUtils} from "../../services/utils/SendUtils";
import dayjs from "dayjs";

interface InquestTableProps {
    data: SimulatedFileTS[],
    pagination: {
        current: number;
        pageSize: number;
        total: number;
    },
    onPaginationChange: (page: number, pageSize: number) => void,
    onTableChange: TableProps<SimulatedFileTS>["onChange"],
    sortField: string | null,
    sortOrder: "ascend" | "descend" | null,
    selectedStatus: StatusCodeE | null,
    onSelectedRows: (selectedRows: SimulatedFileTS[]) => void,
    selectedRowKeys?: React.Key[],
    onSelectionChange?: (keys: React.Key[], rows: SimulatedFileTS[]) => void,
    loading?: any,
    tempestaTab?: boolean,
    onTempestaTabChange?: (tempesta: boolean) => void,
}

export default function CustomTable({
                                        data,
                                        pagination,
                                        onPaginationChange,
                                        onTableChange,
                                        sortField,
                                        sortOrder,
                                        selectedStatus,
                                        onSelectedRows,
                                        selectedRowKeys: controlledSelectedRowKeys,
                                        onSelectionChange,
                                        loading,
                                        tempestaTab,
                                        onTempestaTabChange,
                                    }: Readonly<InquestTableProps>) {

    const [innerSelectedRowKeys, setInnerSelectedRowKeys] = useState<React.Key[]>([])
    const [loadingPersonId, setLoadingPersonId] = useState<number | null>(null);

    const getColumns = (): ColumnsType<SimulatedFileTS> => {
        const baseColumns: ColumnsType<SimulatedFileTS> = [
            {
                title: "Codice Fiscale",
                dataIndex: "fiscalCode",
                key: "fiscalCode",
                sorter: true,
                sortOrder: sortField === "fiscalCode" ? sortOrder : undefined
            },
            {
                title: "Cognome Nome",
                dataIndex: "fullName",
                key: "fullName",
                sorter: true,
                sortOrder: sortField === "fullName" ? sortOrder : undefined
            },
            {
                title: "Anno",
                dataIndex: "years",
                key: "years",
                render: (years: string[]) => years.sort((a, b) => parseInt(a) - parseInt(b)).join(" - "),
                sorter: true,
                sortOrder: sortField === "years" ? sortOrder : undefined,
            },
            {
                title: "Codice esenzione",
                dataIndex: "exemptionCodes",
                key: "exemptionCodes",
                render: ((codes) => codes.join(" - "))
            },
            {
                title: "Tipo",
                dataIndex: "type",
                key: "type",
            },
            {
                title: "Ticket",
                dataIndex: "totalTicket",
                key: "totalTicket",
                sorter: true,
                sortOrder: sortField === "totalTicket" ? sortOrder : undefined,
                render: (val: number) => `€ ${UtilsService.formatNumber(val)}`,
            },
        ];

        const actionColumn = {
            title: "Azioni",
            key: "operation",
            render: (record: SimulatedFileTS) => (
                <div style={{display: "flex", gap: "8px"}}>
                    {[StatusCodeE.NOTIFICA, StatusCodeE.ATTESA_PAGAMENTO, StatusCodeE.PAGATE, StatusCodeE.DA_FIRMARE].includes(selectedStatus!) && (
                        <Tooltip placement="top" title="Visualizza documento">
                            <Button
                                onClick={() => {
                                    setLoadingPersonId(record.personId);
                                    TemplateUtils.downloadGlyphedDocument(
                                        record.simulatorId,
                                        record.personId,
                                        () => setLoadingPersonId(null),
                                        selectedStatus
                                    );
                                }}
                                icon={
                                    loadingPersonId === record.personId ? <Spin size="small"/> : <EyeOutlined/>
                                }
                                disabled={loadingPersonId === record.personId}
                            />
                        </Tooltip>
                    )}
                    <UserDetailsButton personID={record.personId} combinatorID={record.simulatorId}/>
                </div>
            )
        };

        if (selectedStatus && [StatusCodeE.NOTIFICA, StatusCodeE.ATTESA_PAGAMENTO, StatusCodeE.PAGATE, StatusCodeE.TRASFERITE, StatusCodeE.SCADUTE].includes(selectedStatus)) {
            baseColumns.push(
                {
                    title: "No. Protocollo",
                    dataIndex: "protocolNumbers",
                    key: "protocolNumbers",
                    render: ((protocolNumbers) => protocolNumbers.join(" - "))
                }
            )
        }

        if (selectedStatus && [StatusCodeE.NOTIFICA].includes(selectedStatus)) {
            baseColumns.push(
                {
                    title: "Stato SEND",
                    dataIndex: "sendStatus",
                    key: "sendStatus",
                    render: ((value) => (value ?
                        <Typography.Text type={SendUtils.getStatusColor(value as SendStatusType)}>
                            {SendStatusLabel[value as SendStatusType]}</Typography.Text> :
                        <MinusOutlined/>))
                }
            )
        }

        if (selectedStatus && [StatusCodeE.ATTESA_PAGAMENTO, StatusCodeE.SCADUTE].includes(selectedStatus)) {
            baseColumns.push(
                {
                    title: "Data di notifica",
                    dataIndex: "communicationDate",
                    key: "communicationDate",
                    render: (val: string) => val ? dayjs(val).format("DD/MM/YYYY") : "—"
                })
        }

        if (selectedStatus && [StatusCodeE.SOSPESE, StatusCodeE.RENDICONTATE].includes(selectedStatus)) {
            if ([StatusCodeE.RENDICONTATE].includes(selectedStatus)) {
                const indexToRemove = baseColumns.findIndex(column => column.key === 'type');
                if (indexToRemove > -1) {
                    baseColumns.splice(indexToRemove, 1);
                }
            }
            baseColumns.push(
                {
                    title: "Motivazione",
                    dataIndex: "motivazione",
                    key: "motivazione",
                    render: (val: string) => {
                        if (selectedStatus === StatusCodeE.RENDICONTATE) {
                            return getArchiveReasonLabel(val);
                        } else if (selectedStatus === StatusCodeE.SOSPESE) {
                            return getSuspensionReasonLabel(val);
                        }
                        return val;
                    },
                }
            )
        }


        if (selectedStatus && [StatusCodeE.DA_RENDICONTARE].includes(selectedStatus)) {
            const indexToRemove = baseColumns.findIndex(column => column.key === 'type');
            if (indexToRemove > -1) {
                baseColumns.splice(indexToRemove, 1);
            }

            baseColumns.push(
                {
                    title: "Diagnostica TS",
                    dataIndex: "diagnosticoArchiviazioneTS",
                    key: "diagnosticoArchiviazioneTS"
                }
            )
        }

        if (selectedStatus && [StatusCodeE.SCADUTE].includes(selectedStatus)) {
            const indexToRemove = baseColumns.findIndex(column => column.key === 'type');
            if (indexToRemove > -1) {
                baseColumns.splice(indexToRemove, 1);
            }

            baseColumns.push(
                {
                    title: "Scadenza",
                    dataIndex: "deadline",
                    key: "deadline",
                    render: (val: string) => val ? dayjs(val).format("DD/MM/YYYY") : "—"
                },
                {
                    title: "Giorni",
                    dataIndex: "daysOverdue",
                    key: "daysOverdue",
                    sorter: true,
                    sortOrder: sortField === "daysOverdue" ? sortOrder : undefined,
                    render: (_, record: SimulatedFileTS) => {
                        const val = record.deadline;
                        if (!val) return "—";
                        const days = dayjs().diff(dayjs(val), 'day');
                        let color: string | undefined;
                        if (days >= 91) color = "#ff4d4f";
                        else if (days >= 20) color = "#fa8c16";
                        else if (days >= 10) color = "#faad14";
                        return <span style={color ? {color, fontWeight: "bold"} : undefined}>{days}</span>;
                    }
                }
            )
        }
        return (selectedStatus === StatusCodeE.DA_LAVORARE || selectedStatus === StatusCodeE.IN_LAVORAZIONE || selectedStatus === StatusCodeE.RESIDUE || selectedStatus === StatusCodeE.DA_RENDICONTARE || selectedStatus === StatusCodeE.RENDICONTATE)
            ? baseColumns
            : [...baseColumns, actionColumn];
    };

    const rowSelection: TableProps<SimulatedFileTS>['rowSelection'] = {
        selectedRowKeys: controlledSelectedRowKeys ?? innerSelectedRowKeys,
        onChange: (keys: React.Key[], rows: SimulatedFileTS[]) => {
            if (controlledSelectedRowKeys === undefined) {
                setInnerSelectedRowKeys(keys);
            }
            onSelectedRows(rows);
            onSelectionChange?.(keys, rows);
        },
    };

    if (!selectedStatus) return null;

    const renderTable = (tableData: SimulatedFileTS[]) => (
        <Table<SimulatedFileTS>
            rowSelection={{type: "checkbox", ...rowSelection}}
            columns={getColumns()}
            dataSource={tableData}
            bordered
            pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showTotal: (total) => `Totale ${total} elementi`,
                onChange: onPaginationChange
            }}
            onChange={onTableChange}
            className="styled-table"
            rowKey={(record) => `${record.simulatorId}_${record.personId}_${record.index ?? 0}`}
            loading={loading}
        />
    );

    if (selectedStatus && [StatusCodeE.SCADUTE, StatusCodeE.DA_TRASFERIRE, StatusCodeE.TRASFERITE].includes(selectedStatus)) {
        return (
            <Tabs
                activeKey={tempestaTab === false ? "diffida" : "verbale"}
                onChange={(key) => onTempestaTabChange?.(key === "verbale")}
                items={[
                    {
                        key: "diffida",
                        label: "Diffida",
                        children: renderTable(data)
                    },
                    {
                        key: "verbale",
                        label: "Verbale",
                        children: renderTable(data)
                    },
                ]}
            />
        );
    }

    return renderTable(data);
}
